/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Send/Receive page components
import SendWidget from "layouts/billing/components/SendWidget";
import ReceiveWidget from "layouts/billing/components/ReceiveWidget";
import BalanceOverview from "layouts/billing/components/BalanceOverview";
import RecentTransactions from "layouts/billing/components/RecentTransactions";

// Wallet Services
import walletService from "services/walletService";
import storageService from "services/storageService";
import mytherraApi from "services/mytherraApi";

function Billing() {
  const history = useHistory();
  const [balance, setBalance] = useState(0);
  const [walletAddress, setWalletAddress] = useState("");
  const [walletUtxos, setWalletUtxos] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mytPrice, setMytPrice] = useState(null);

  // RPC Configuration
  const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3002' : '/api';
  const RPC_URL = `${API_BASE}/rpc`;
  const RPC_USER = '';
  const RPC_PASSWORD = '';

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        setLoading(true);

        // Check if user has a session
        const privateKey = storageService.loadSession();
        
        if (!privateKey) {
          console.log('No wallet session found, redirecting to sign in');
          history.push('/sign-in');
          return;
        }

        // Get address from private key
        const address = await walletService.getAddressFromWif(privateKey);
        
        if (!address) {
          console.error('Invalid private key in session');
          storageService.clearSession();
          history.push('/sign-in');
          return;
        }

        setWalletAddress(address);

        // Load UTXOs and calculate balance
        const utxos = await walletService.getUtxos(RPC_URL, RPC_USER, RPC_PASSWORD, address);
        setWalletUtxos(utxos);
        
        const bal = walletService.calculateBalance(utxos);
        setBalance(bal);

        // If no UTXOs from RPC, try Explorer for balance
        if (utxos.length === 0) {
          const explorerBalance = await walletService.getBalanceFromExplorer(address);
          if (explorerBalance !== null) {
            setBalance(explorerBalance);
          }
        }

        // Fetch MYT price
        try {
          const response = await fetch(`${API_BASE}/price`);
          if (response.ok) {
            const data = await response.json();
            setMytPrice(data.price);
          }
        } catch (err) {
          console.log('Price fetch failed:', err);
        }

        // Fetch recent transactions for this specific address from Explorer
        try {
          const addressData = await mytherraApi.getExplorerAddress(address);
          console.log('Billing - Address data from explorer:', addressData);
          
          if (addressData && addressData.txs && Array.isArray(addressData.txs) && addressData.txs.length > 0) {
            // txs is an array of transaction IDs, fetch details via Explorer API
            const txDetails = await Promise.all(
              addressData.txs.slice(0, 10).map(async (txid) => {
                try {
                  // Use Explorer API via proxy to get transaction details
                  const response = await fetch(`${API_BASE}/explorer/api/getrawtransaction?txid=${txid}&decrypt=1`);
                  if (!response.ok) {
                    console.log(`Failed to fetch tx ${txid}: ${response.status}`);
                    return null;
                  }
                  
                  const txData = await response.json();
                  if (!txData || txData.error) {
                    console.log(`Error in tx data for ${txid}:`, txData?.error);
                    return null;
                  }
                  
                  // Determine if this is a send or receive
                  const isInputTx = addressData.input_txs && addressData.input_txs.includes(txid);
                  
                  let amount = 0;
                  let category = 'receive';
                  
                  if (isInputTx) {
                    category = 'send';
                    for (const vout of txData.vout || []) {
                      if (vout.scriptPubKey && vout.scriptPubKey.address !== address) {
                        amount += vout.value;
                      }
                    }
                    amount = -amount;
                  } else {
                    for (const vout of txData.vout || []) {
                      if (vout.scriptPubKey && vout.scriptPubKey.address === address) {
                        amount += vout.value;
                      }
                    }
                  }
                  
                  // Get timestamp - try multiple sources
                  let timestamp = txData.time || txData.blocktime;
                  
                  // If no timestamp and we have a blockhash, fetch the block to get the time
                  if (!timestamp && txData.blockhash) {
                    try {
                      const blockResponse = await fetch(`${API_BASE}/explorer/api/getblock?hash=${txData.blockhash}`);
                      if (blockResponse.ok) {
                        const blockData = await blockResponse.json();
                        timestamp = blockData.time || blockData.blocktime;
                      }
                    } catch (blockErr) {
                      console.log('Could not fetch block time for', txData.blockhash);
                    }
                  }
                  
                  // Last resort: use current time (but log a warning)
                  if (!timestamp) {
                    console.warn('No timestamp found for tx', txid, '- using current time');
                    timestamp = Date.now() / 1000;
                  }
                  
                  return {
                    txid: txid,
                    amount: amount,
                    category: category,
                    confirmations: txData.confirmations || 0,
                    time: timestamp
                  };
                } catch (err) {
                  console.log('Could not fetch tx details for', txid, err);
                  return null;
                }
              })
            );
            
            // Filter out null values and sort by time
            const txs = txDetails.filter(tx => tx !== null);
            txs.sort((a, b) => b.time - a.time);
            
            console.log('Billing - Parsed transactions:', txs);
            setTransactions(txs);
          } else {
            console.log('No address data returned from explorer');
            setTransactions([]);
          }
        } catch (explorerError) {
          console.log('Explorer API not available for this address (possibly Bech32 format not supported), using UTXOs as fallback');
          
          // Fallback: Convert UTXOs to transaction format with real timestamps
          if (utxos && utxos.length > 0) {
              const txList = await Promise.all(
                utxos.map(async (utxo) => {
                  try {
                    const txDetails = await mytherraApi.rpcCall('getrawtransaction', [utxo.txid, true]);
                    return {
                      txid: utxo.txid,
                      vout: utxo.vout,
                      amount: utxo.amount,
                      confirmations: utxo.confirmations,
                      category: 'receive',
                      time: txDetails?.time || txDetails?.blocktime || Date.now() / 1000
                    };
                  } catch (err) {
                    console.log('Could not fetch tx details for', utxo.txid);
                    return {
                      txid: utxo.txid,
                      vout: utxo.vout,
                      amount: utxo.amount,
                      confirmations: utxo.confirmations,
                      category: 'receive',
                      time: Date.now() / 1000
                    };
                  }
                })
              );
              // Sort by time (newest first)
              txList.sort((a, b) => b.time - a.time);
              setTransactions(txList.slice(0, 10));
          } else {
            setTransactions([]);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching wallet data:", error);
        setLoading(false);
      }
    };

    fetchWalletData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchWalletData, 30000);

    return () => clearInterval(interval);
  }, [history]);

  const handleSendSuccess = async () => {
    // Refresh wallet data after successful send
    const privateKey = storageService.loadSession();
    if (privateKey) {
      const address = await walletService.getAddressFromWif(privateKey);
      if (address) {
        const utxos = await walletService.getUtxos(RPC_URL, RPC_USER, RPC_PASSWORD, address);
        setWalletUtxos(utxos);
        const bal = walletService.calculateBalance(utxos);
        setBalance(bal);
        
        // Refresh transactions for this specific address
        const addressData = await mytherraApi.getExplorerAddress(address);
        if (addressData && addressData.transactions) {
          const recentTxs = addressData.transactions.slice(0, 10);
          setTransactions(recentTxs);
        }
      }
    }
  };

  const handleGenerateAddress = async () => {
    // For client-side wallet, we use the same address from private key
    // You could implement HD wallet (BIP32) to generate multiple addresses if needed
    console.log('Current address:', walletAddress);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox mt={4}>
        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} lg={5}>
            <VuiBox mb={3}>
              <BalanceOverview balance={balance} loading={loading} mythPrice={mytPrice} />
            </VuiBox>
            <VuiBox>
              <ReceiveWidget 
                address={walletAddress} 
                onGenerateAddress={handleGenerateAddress}
                loading={loading}
              />
            </VuiBox>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} lg={7}>
            <VuiBox mb={3}>
              <SendWidget balance={balance} onSendSuccess={handleSendSuccess} />
            </VuiBox>
            <VuiBox>
              <RecentTransactions transactions={transactions} loading={loading} />
            </VuiBox>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Billing;
